/* =============================================================================
   Storm Light — premium-elevation layer (P2 · P4a · P5d · P5e).

   The optional polish stratum, kept OUT of ws-app.js on purpose: ws-app.js is
   the core interactive bundle (drawers, variant sync, cart, reveals) guarded by
   a hard minified-size budget (R-01, bin/ws-check). These four modules are a
   cohesive, independently-reversible enhancement layer — site-wide typographic
   refinement, the two interrupt-modals' retiming, and the grid→PDP morph — so
   they ship as their own deferred module rather than growing the guarded core.
   Delete this file + its <script> tag in theme.liquid and the store falls back
   cleanly; nothing here is required to shop.

   Progressive enhancement throughout. No framework. Self-contained IIFEs with
   no dependency on ws-app.js (execution order is irrelevant).
   ============================================================================= */


/* ------------------------------------------------------------- smart quotes */
/* P4a — typographic apostrophes and quotation marks across the whole site,
   applied at the text-node level so it covers hardcoded strings, section
   settings AND rich-text/metafield prose uniformly, with one rule and no CMS
   fight. Reversible: remove this module and every quote goes back to a prime.

   THE SPEC REGISTER KEEPS STRAIGHT PRIMES ON PURPOSE. Coordinates, spec lists,
   Log Lines and every other mono/uppercase label live in .ws-micro (the one
   monospace register). A prime is the correct glyph there — 47°36'N is a
   measurement, not a quotation — so .ws-micro and anything monospaced is
   skipped, exactly as the plan requires. Editable fields, code and pre are
   skipped too: you never curl what a user is typing.

   Once only. It walks the DOM a single time after parse; it does not observe. */
(function smartQuotes() {
  const SKIP = new Set([
    'SCRIPT', 'STYLE', 'NOSCRIPT', 'CODE', 'PRE', 'KBD', 'SAMP', 'VAR',
    'TEXTAREA', 'INPUT', 'SELECT', 'OPTION', 'SVG', 'MATH',
  ]);

  // A prime is intentional in the mono/spec register and in editable fields.
  const skipEl = (el) =>
    SKIP.has(el.tagName) ||
    el.isContentEditable ||
    el.classList.contains('ws-micro') ||
    el.closest('.ws-micro, [contenteditable], [data-ws-primes]') !== null;

  const curl = (s) =>
    s
      // Opening double quote: at a boundary (start / space / open bracket / dash).
      .replace(/(^|[\s([{–—‘])"/g, '$1“')
      // Any remaining double quote is a closer.
      .replace(/"/g, '”')
      // Opening single quote at a boundary.
      .replace(/(^|[\s([{–—])'/g, '$1‘')
      // Every other apostrophe (contraction, possessive, closer) is a right single.
      .replace(/'/g, '’');

  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      // Nothing to do if there is no straight quote to convert.
      if (!/['"]/.test(node.nodeValue)) return NodeFilter.FILTER_REJECT;
      const parent = node.parentElement;
      if (!parent || skipEl(parent)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  const targets = [];
  let n;
  while ((n = walker.nextNode())) targets.push(n);
  targets.forEach((node) => {
    const next = curl(node.nodeValue);
    if (next !== node.nodeValue) node.nodeValue = next;
  });
})();

/* --------------------------------------------------------- media blur-up */
/* P5e — decode-gated reveal on product media. A frame stays at opacity 0 over
   its own harbor-mist / bone ground until the browser has actually DECODED it,
   then fades in over --ws-duration (~350ms). This kills the hard lazy pop-in
   without adding a single request: the ground already there IS the dominant-
   colour placeholder, so the grid's request budget is untouched (a blur bitmap
   was considered and declined here for that reason — see build notes).

   FAIL-OPEN, like the reveal system: the hidden start-state is gated on a
   JS-added class (.ws-media-ready) that is set only AFTER this wires the frames,
   and any frame already complete at wire time is marked decoded synchronously so
   it never blinks. No-JS, a throw, or a blocked module leaves every frame
   visible. The LCP hero is deliberately NOT tagged (it must never be blurry). */
(function mediaBlurUp() {
  const frames = document.querySelectorAll('img[data-ws-media-reveal]');
  if (!frames.length) {
    document.documentElement.classList.add('ws-media-ready');
    return;
  }

  const show = (img) => img.setAttribute('data-decoded', 'true');

  frames.forEach((img) => {
    // Already loaded before we got here — reveal now, before the hide-gate lands,
    // so it never flashes from visible to hidden to visible.
    if (img.complete && img.naturalWidth > 0) {
      show(img);
      return;
    }
    const reveal = () => {
      if ('decode' in img) {
        img.decode().then(() => show(img)).catch(() => show(img));
      } else {
        show(img);
      }
    };
    img.addEventListener('load', reveal, { once: true });
    // A broken image must not be stranded invisible.
    img.addEventListener('error', () => show(img), { once: true });
  });

  // Only now may the hidden start-state apply (mirrors .ws-reveal-ready).
  document.documentElement.classList.add('ws-media-ready');
})();

/* ------------------------------------------------ product-card → PDP morph */
/* P5d — the click side of the grid→PDP shared-element view transition. The card
   images already carry data-view-transition-type="product-image-transition"
   (grid context, and only when the setting is on). On navigation Horizon's
   view-transitions.js reads whichever element is data-view-transition-triggered
   and morphs its shared name into the PDP hero. This tags the CLICKED card's
   image so exactly one element on the page ever holds the name — no duplicate-
   name abort. All the reduced-motion + in-app-WebView guards are Horizon's;
   this only marks the source element. The ~0.4s expo timing is CSS (ws-base). */
(function cardMorphTrigger() {
  document.addEventListener(
    'click',
    (e) => {
      const link = e.target.closest?.('.ws-card__link, .ws-card a[href]');
      if (!link) return;
      const card = link.closest('.ws-card');
      const img = card?.querySelector('.ws-card__img[data-view-transition-type]');
      if (!img) return;
      img.setAttribute('data-view-transition-triggered', 'true');
    },
    // Capture so we tag before the navigation begins.
    true
  );
})();

/* --------------------------------------------------------- klaviyo retime */
/* P2 — the Klaviyo popup must never fire OVER the giving ledger (the mission
   band) and must not slam the hero's first screen. Every .klaviyo-form on this
   store is the onsite POPUP — the WS inline signup posts to Shopify's native
   endpoint, not Klaviyo — so the whole class can be held safely.

   Hold, then release: body.ws-klaviyo-hold suppresses the popup (CSS, in
   ws-interrupt-modals). The gate opens once the visitor has scrolled PAST the
   mission band, and never while that band is on screen, with a hard fallback so
   the popup is never held forever. Fail-open: the hard cap is armed FIRST and
   any error releases the hold, so a bug here can never permanently hide capture.

   NOTE: the authoritative trigger timing lives in Klaviyo's own form editor
   (account-side, founder-gated). This is the theme-side guarantee that whatever
   Klaviyo is set to, it will not land over the moral centre of the page. */
(function klaviyoRetime() {
  const body = document.body;
  const band = document.querySelector('[data-ws-mission-band]');
  // Longer patience when a mission band exists (wait to pass it); shorter when
  // there is none (other templates just get a brief "let them land" delay).
  const FALLBACK_MS = band ? 18000 : 8000;

  body.classList.add('ws-klaviyo-hold');

  let opened = false;
  let bandVisible = false;
  let pastBand = !band; // no band on this page => nothing to wait to pass
  let delayElapsed = false;
  let io = null;

  const release = () => {
    if (opened) return;
    opened = true;
    body.classList.remove('ws-klaviyo-hold');
    io?.disconnect();
    clearTimeout(hardCap);
  };

  const maybeOpen = () => {
    if (opened || bandVisible) return; // never release while over the ledger
    if (pastBand || delayElapsed) release();
  };

  // Armed first: whatever else happens, the hold is finite.
  const hardCap = setTimeout(() => {
    delayElapsed = true;
    // If we are sitting on the ledger at the cap, wait for it to leave; otherwise
    // release now. A second, absolute cap guarantees eventual release regardless.
    maybeOpen();
    if (!opened) setTimeout(release, 6000);
  }, FALLBACK_MS);

  try {
    if (band && 'IntersectionObserver' in window) {
      io = new IntersectionObserver(
        ([entry]) => {
          bandVisible = entry.isIntersecting;
          // Scrolled past when the band has left the top of the viewport.
          if (!entry.isIntersecting && entry.boundingClientRect.top < 0) pastBand = true;
          maybeOpen();
        },
        { threshold: 0 }
      );
      io.observe(band);
    }
  } catch (err) {
    release(); // any failure = show the popup rather than hide it forever
  }
})();
